/**
 * Email Template Manager Component
 * Allows users to create, edit, view, and delete custom email templates for OA reporting
 */

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Wand2, Loader2 } from 'lucide-react';
import { userApi, documentApi, patentApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface EmailTemplate {
  prompt_id: string;
  name: string;
  description: string;
  template_structure: {
    greeting: string;
    intro: string;
    sections: string[];
    closing: string;
    signature: string;
    tone: string;
    use_standard_sections?: boolean;
  };
  created_at: string;
}

const STANDARD_SECTIONS = [
  'KEY INFORMATION',
  'CLAIMS STATUS',
  'REJECTION DETAILS',
  'PRIOR ART CITED',
  'FORMAL OBJECTIONS',
  'RECOMMENDED NEXT STEPS',
  'IMPORTANT DEADLINES'
];

export const EmailTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    greeting: 'Dear [Client Name],',
    intro: '',
    sections: [] as string[],
    closing: '',
    signature: 'Best regards,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Contact Information]',
    tone: 'Professional',
    use_standard_sections: false,
  });
  const [sectionsInput, setSectionsInput] = useState('');
  const [selectedStandardSections, setSelectedStandardSections] = useState<string[]>([]);

  // Auto-generate state
  const [rawEmailText, setRawEmailText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Notification modal state
  const [notificationModal, setNotificationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const showNotification = (title: string, message: string, type: 'success' | 'error' = 'success') => {
    setNotificationModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await userApi.fetchEmailTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Parse sections: use selected standard sections if enabled, otherwise custom sections
      const sectionsArray = formData.use_standard_sections
        ? selectedStandardSections
        : sectionsInput.split(',').map(s => s.trim()).filter(s => s.length > 0);

      await userApi.saveEmailTemplate(
        formData.name,
        formData.description,
        {
          greeting: formData.greeting,
          intro: formData.intro,
          sections: sectionsArray,
          closing: formData.closing,
          signature: formData.signature,
          tone: formData.tone,
          use_standard_sections: formData.use_standard_sections,
        },
        editingTemplate?.prompt_id
      );

      // Refresh templates list
      await fetchTemplates();

      // Close dialog and reset form
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save email template:', error);
      showNotification('Error', 'Failed to save template. Please try again.', 'error');
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await userApi.deleteEmailTemplate(templateId);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to delete email template:', error);
      showNotification('Error', 'Failed to delete template. Please try again.', 'error');
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    const useStandardSections = template.template_structure.use_standard_sections || false;
    setFormData({
      name: template.name,
      description: template.description,
      greeting: template.template_structure.greeting,
      intro: template.template_structure.intro,
      sections: template.template_structure.sections,
      closing: template.template_structure.closing,
      signature: template.template_structure.signature,
      tone: template.template_structure.tone || 'Professional',
      use_standard_sections: useStandardSections,
    });

    if (useStandardSections) {
      // If using standard sections, set selected ones from template
      setSelectedStandardSections(template.template_structure.sections);
      setSectionsInput(template.template_structure.sections.join(', '));
    } else {
      setSelectedStandardSections([]);
      setSectionsInput(template.template_structure.sections.join(', '));
    }

    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      greeting: 'Dear [Client Name],',
      intro: '',
      sections: [],
      closing: '',
      signature: 'Best regards,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Contact Information]',
      tone: 'Professional',
      use_standard_sections: false,
    });
    setSectionsInput('');
    setSelectedStandardSections([]);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
    setRawEmailText('');
  };

  const handleAutoGenerate = async () => {
    if (!rawEmailText.trim()) {
      showNotification('Input Required', 'Please paste an email to analyze', 'error');
      return;
    }

    setIsGenerating(true);
    try {
      // Step 1: Initiate the auto-generation task
      const response = await userApi.autoGenerateEmailTemplate(rawEmailText);

      console.log('Auto-generate started:', response);

      // Check if we got a job_id
      if (!response || !response.job_id) {
        console.error('Invalid response structure:', response);
        showNotification('Error', 'Failed to start template generation. Please try again.', 'error');
        setIsGenerating(false);
        return;
      }

      const jobId = response.job_id;

      // Step 2: Poll for task completion
      const maxAttempts = 60; // 60 attempts * 2 seconds = 2 minutes max
      let attempts = 0;
      let status = 'Processing';

      while (attempts < maxAttempts && status !== 'Complete' && status !== 'Failed') {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds

        const statusResponse = await documentApi.getJobStatus(jobId);
        status = statusResponse?.status || statusResponse;

        console.log(`Polling attempt ${attempts + 1}: status = ${status}`);
        attempts++;
      }

      // Step 3: Check if task completed successfully
      if (status !== 'Complete') {
        showNotification(
          'Generation Failed',
          `Template generation ${status === 'Failed' ? 'failed' : 'timed out'}. Please try again.`,
          'error'
        );
        setIsGenerating(false);
        return;
      }

      // Step 4: Retrieve the generated template
      const result = await patentApi.getCustomAnalysisResult(jobId);

      console.log('Auto-generate result:', result);

      // Parse the analysis field (which contains the JSON result)
      let templateData;
      try {
        if (typeof result.analysis === 'string') {
          // Try to parse as JSON first
          try {
            templateData = JSON.parse(result.analysis);
          } catch (jsonError) {
            // If JSON parse fails, try converting Python dict format to JSON
            // Replace single quotes with double quotes for JSON compatibility
            const jsonString = result.analysis
              .replace(/'/g, '"')
              .replace(/None/g, 'null')
              .replace(/True/g, 'true')
              .replace(/False/g, 'false');
            templateData = JSON.parse(jsonString);
          }
        } else {
          templateData = result.analysis;
        }
      } catch (e) {
        console.error('Failed to parse template data:', e, 'Raw data:', result.analysis);
        showNotification('Parse Error', 'Failed to parse generated template. Please try again.', 'error');
        setIsGenerating(false);
        return;
      }

      // Check if we have the expected structure
      if (!templateData || !templateData.template_structure) {
        console.error('Invalid template data structure:', templateData);
        showNotification('Invalid Data', 'Received invalid template data. Please try again.', 'error');
        setIsGenerating(false);
        return;
      }

      const templateStruct = templateData.template_structure;

      // Pre-fill the form with generated template
      setFormData({
        name: templateData.name || 'Auto-generated Template',
        description: templateData.description || '',
        greeting: templateStruct.greeting || 'Dear [Client Name],',
        intro: templateStruct.intro || '',
        sections: templateStruct.sections || [],
        closing: templateStruct.closing || '',
        signature: templateStruct.signature || '',
        tone: templateStruct.tone || 'Professional',
        use_standard_sections: false, // Auto-generated templates use custom sections by default
      });
      setSectionsInput(templateStruct.sections?.join(', ') || '');

      // Show success message
      showNotification('Success', 'Template generated successfully! Review and save below.', 'success');
    } catch (error) {
      console.error('Failed to auto-generate template:', error);
      showNotification('Error', 'Failed to generate template. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Email Templates</h2>
          <p className="text-gray-600 mt-1">
            Create and manage custom templates for Office Action reporting emails
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Email Template' : 'Create New Email Template'}
              </DialogTitle>
              <DialogDescription>
                {editingTemplate ? 'Update your custom email template' : 'Create manually or auto-generate from a past email'}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="manual" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual">Manual</TabsTrigger>
                <TabsTrigger value="auto" disabled={!!editingTemplate}>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-Generate
                </TabsTrigger>
              </TabsList>

              <TabsContent value="auto" className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="rawEmail">Paste Past Email</Label>
                  <Textarea
                    id="rawEmail"
                    value={rawEmailText}
                    onChange={(e) => setRawEmailText(e.target.value)}
                    placeholder="Paste a complete email message here. The AI will analyze it and extract a reusable template structure..."
                    rows={12}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Paste a complete Office Action reporting email. The AI will identify patterns, extract placeholders, and create a template.
                  </p>
                </div>

                <Button
                  onClick={handleAutoGenerate}
                  disabled={isGenerating || !rawEmailText.trim()}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Email...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Template from Email
                    </>
                  )}
                </Button>

                {formData.name && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">
                      Template generated! Switch to the "Manual" tab to review and save.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="manual" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Custom Template"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when to use this template"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="greeting">Greeting</Label>
                <Input
                  id="greeting"
                  value={formData.greeting}
                  onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                  placeholder="Dear [Client Name],"
                />
                <p className="text-xs text-gray-500">
                  Use placeholders like [Client Name] that will be replaced with actual data
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="intro">Introduction Paragraph *</Label>
                <Textarea
                  id="intro"
                  value={formData.intro}
                  onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
                  placeholder="We received a [Action Type] dated [Mailing Date]..."
                  rows={3}
                />
                <p className="text-xs text-gray-500">
                  Use placeholders: [Action Type], [Mailing Date], [Application Number], etc.
                </p>
              </div>

              <div className="space-y-3">
                <Label htmlFor="sections">Email Sections *</Label>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="use-standard-sections"
                    checked={formData.use_standard_sections}
                    onCheckedChange={(checked) => {
                      const isChecked = !!checked;
                      setFormData({ ...formData, use_standard_sections: isChecked });
                      if (isChecked) {
                        // When enabling, select all standard sections by default
                        setSelectedStandardSections([...STANDARD_SECTIONS]);
                        setSectionsInput(STANDARD_SECTIONS.join(', '));
                      } else {
                        setSelectedStandardSections([]);
                      }
                    }}
                  />
                  <label
                    htmlFor="use-standard-sections"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Use standard OA reporting sections
                  </label>
                </div>

                {formData.use_standard_sections && (
                  <div className="border rounded-lg p-4 space-y-2 bg-gray-50">
                    <p className="text-sm font-semibold mb-2">Select Standard Sections:</p>
                    <div className="grid grid-cols-1 gap-2">
                      {STANDARD_SECTIONS.map((section) => (
                        <div key={section} className="flex items-center space-x-2">
                          <Checkbox
                            id={`section-${section}`}
                            checked={selectedStandardSections.includes(section)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                const newSections = [...selectedStandardSections, section];
                                setSelectedStandardSections(newSections);
                                setSectionsInput(newSections.join(', '));
                              } else {
                                const newSections = selectedStandardSections.filter(s => s !== section);
                                setSelectedStandardSections(newSections);
                                setSectionsInput(newSections.join(', '));
                              }
                            }}
                          />
                          <label
                            htmlFor={`section-${section}`}
                            className="text-sm cursor-pointer"
                          >
                            {section}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!formData.use_standard_sections && (
                  <>
                    <Input
                      id="sections"
                      value={sectionsInput}
                      onChange={(e) => setSectionsInput(e.target.value)}
                      placeholder="KEY INFORMATION, CLAIMS STATUS, NEXT STEPS"
                    />
                    <p className="text-xs text-gray-500">
                      Comma-separated list of section names in ALL CAPS
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="closing">Closing Paragraph *</Label>
                <Textarea
                  id="closing"
                  value={formData.closing}
                  onChange={(e) => setFormData({ ...formData, closing: e.target.value })}
                  placeholder="Please let us know if you would like to schedule a call..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signature">Signature</Label>
                <Textarea
                  id="signature"
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tone">Tone Description</Label>
                <Input
                  id="tone"
                  value={formData.tone}
                  onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                  placeholder="Professional and concise"
                />
                <p className="text-xs text-gray-500">
                  Describe the tone to guide the AI (e.g., "Professional", "Client-friendly", "Technical")
                </p>
              </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!formData.name || !formData.intro || sectionsInput.trim() === '' || !formData.closing}
              >
                <Save className="w-4 h-4 mr-2" />
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No custom templates yet</p>
            <p className="text-sm text-gray-400 mt-2">
              Create your first template to get started
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="max-h-[600px] overflow-y-auto space-y-3 pr-2">
          {templates.map((template) => (
            <Card key={template.prompt_id} className="w-full">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {template.description && (
                      <CardDescription className="mt-1">
                        {template.description}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(template)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.prompt_id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-semibold">Tone:</span>{' '}
                    {template.template_structure.tone}
                  </div>
                  <div>
                    <span className="font-semibold">Sections:</span>{' '}
                    {template.template_structure.sections.length}
                  </div>
                  <div className="text-xs text-gray-500 ml-auto">
                    {new Date(template.created_at).toLocaleDateString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Notification Modal */}
      <Dialog open={notificationModal.isOpen} onOpenChange={(open) => setNotificationModal({ ...notificationModal, isOpen: open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={notificationModal.type === 'error' ? 'text-red-600' : 'text-green-600'}>
              {notificationModal.title}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700">{notificationModal.message}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setNotificationModal({ ...notificationModal, isOpen: false })}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
